import { faCalendar } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { FormattedMessage, FormattedPlural } from 'react-intl';

import buildLocalizedDateRangeLabel from 'src/util/time/dateRange';

import { LoadingMessageKeys, PropertyShowLoadingStates } from '../reducer/constants';

/**
 * Generates loading overlay configuration based on the current loading state.
 * The reducer is the single source of truth for which message and progress
 * value should be displayed at any given state.
 *
 * The overlay is visible during all loading stages (1-7), including the COMPLETE state
 * which displays the final "Data prepared and rendered" message. The overlay is hidden
 * only when transitioning to IDLE (via HIDE_OVERLAY action) or ERROR state.
 * @param {object} params - The parameters object.
 * @param {object} params.intl - The react-intl intl object.
 * @param {object} params.datespan - Object with start and finish DateTime for date range display.
 * @param {string} params.timezone - Timezone for date formatting.
 * @param {object} params.loadingState - The current loading state from the reducer.
 * @returns {{ loadingOverlayProps: object|null, isOverlayVisible: boolean }} Overlay configuration
 * with loadingOverlayProps and isOverlayVisible properties.
 */
export const buildLoadingOverlayState = ({
  intl,
  datespan,
  timezone,
  loadingState,
}) => {
  const { state, metadata, overlay } = loadingState;
  const { messageKey, progressBarValue } = overlay || {};
  const {
    metersCount, rulesCount, batchesLoaded, batchesTotal,
  } = metadata;

  // Header and body labels
  const headerLabel = (
    <FormattedMessage
      id="property.property_show.helpers.build_loading_overlay_state.header.message"
      defaultMessage="{calenderIcon} Applying new date range"
      values={{
        calenderIcon: <FontAwesomeIcon icon={faCalendar} />,
      }}
    />
  );

  const bodyLabel = intl.formatMessage({
    id: 'property.property_show.helpers.build_loading_overlay_state.body.message',
    defaultMessage: 'Updating to show {dateRange}.',
  }, {
    dateRange: buildLocalizedDateRangeLabel(
      intl,
      datespan.start,
      datespan.finish,
      timezone,
    ),
  });

  // Stage 1: Property loaded
  const propertyLoadedProgressLabel = intl.formatMessage({
    id: 'property.property_show.helpers.build_loading_overlay_state.stage_1.message',
    defaultMessage: 'Stage 1 of 7 completed. Property Loaded. Loading meters',
  });

  // Stage 2: Meters loaded
  const singleMeter = intl.formatMessage({
    id: 'property.property_show.helpers.build_loading_overlay_state.meter.singular',
    defaultMessage: 'meter',
  });
  const multipleMeters = intl.formatMessage({
    id: 'property.property_show.helpers.build_loading_overlay_state.meter.plural',
    defaultMessage: 'meters',
  });
  const metersLoadedProgressLabel = intl.formatMessage(
    {
      id: 'property.property_show.helpers.build_loading_overlay_state.stage_2.message',
      defaultMessage: 'Stage 2 of 7 completed. Loaded {loadedMeters} {meterCountLabel}. Loading trade rules',
    },
    {
      loadedMeters: <strong>{metersCount}</strong>,
      meterCountLabel: (
        <FormattedPlural value={metersCount} one={singleMeter} other={multipleMeters} />
      ),
    },
  );

  // Stage 3: Rules loaded
  const singleRule = intl.formatMessage({
    id: 'property.property_show.helpers.build_loading_overlay_state.rule.singular',
    defaultMessage: 'trade rule',
  });
  const multipleRules = intl.formatMessage({
    id: 'property.property_show.helpers.build_loading_overlay_state.rule.plural',
    defaultMessage: 'trade rules',
  });
  const rulesLoadedProgressLabel = intl.formatMessage(
    {
      id: 'property.property_show.helpers.build_loading_overlay_state.stage_3.message',
      defaultMessage: 'Stage 3 of 7 completed. Loaded {loadedRules} {rulesCountLabel}. Loading consumed meter data',
    },
    {
      loadedRules: <strong>{rulesCount}</strong>,
      rulesCountLabel: (
        <FormattedPlural value={rulesCount} one={singleRule} other={multipleRules} />
      ),
    },
  );

  // Stage 4: Consumed data loaded
  const consumedDataLoadedProgressLabel = intl.formatMessage({
    id: 'property.property_show.helpers.build_loading_overlay_state.stage_4.message',
    defaultMessage: 'Stage 4 of 7 completed. Loaded consumed meter data. Loading generated meter data',
  });

  // Stage 5: Trade summary progress
  const tradeSummaryProgressLabel = intl.formatMessage({
    id: 'property.property_show.helpers.build_loading_overlay_state.stage_5.message',
    defaultMessage: 'Stage 5 of 7 completed. Loaded generated meter data. Loading {batchLoaded} of {batchTotal} trade summary',
  }, { batchLoaded: batchesLoaded, batchTotal: batchesTotal });

  // Stage 6: Rendering (Preparing data and rendering)
  const renderingProgressLabel = intl.formatMessage({
    id: 'property.property_show.helpers.build_loading_overlay_state.stage_6.rendering.message',
    defaultMessage: 'Stage 6 of 7 completed. Preparing data and rendering',
  });

  // Stage 7: Complete (Data prepared and rendered)
  const completeProgressLabel = intl.formatMessage({
    id: 'property.property_show.helpers.build_loading_overlay_state.stage_7.complete.message',
    defaultMessage: 'Stage 7 of 7 completed. Data prepared and rendered',
  });

  /**
   * Gets the progress label based on the message key from the reducer state.
   * @param {string} key - The message key from LoadingMessageKeys.
   * @returns {string} The formatted progress label.
   */
  const getLoadingMessage = (key) => {
    switch (key) {
      case LoadingMessageKeys.LOADING_PROPERTY:
        return propertyLoadedProgressLabel;
      case LoadingMessageKeys.METERS_LOADED:
        return metersLoadedProgressLabel;
      case LoadingMessageKeys.RULES_LOADED:
        return rulesLoadedProgressLabel;
      case LoadingMessageKeys.CONSUMED_DATA_LOADED:
        return consumedDataLoadedProgressLabel;
      case LoadingMessageKeys.TRADE_SUMMARY_PROGRESS:
        return tradeSummaryProgressLabel;
      case LoadingMessageKeys.RENDERING:
        return renderingProgressLabel;
      case LoadingMessageKeys.COMPLETE:
        return completeProgressLabel;
      default:
        return '';
    }
  };

  // Derive isOverlayVisible from the loading state
  // Overlay is visible during loading, RENDERING, and COMPLETE states
  // IDLE and ERROR are terminal states where overlay is hidden
  const { IDLE, ERROR } = PropertyShowLoadingStates;
  const isOverlayVisible = state !== IDLE && state !== ERROR;

  if (!messageKey || messageKey === LoadingMessageKeys.NONE) {
    return { loadingOverlayProps: null, isOverlayVisible };
  }

  const progressLabel = getLoadingMessage(messageKey);

  return {
    loadingOverlayProps: {
      headerLabel,
      bodyLabel,
      progressLabel,
      progressBarValue,
      updateProgress: () => {},
    },
    isOverlayVisible,
  };
};

export default buildLoadingOverlayState;
